import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Input } from 'src/components/ui/input';
import { Calendar as CalendarIcon, UserIcon } from 'lucide-react';
import { format, subYears } from 'date-fns';
import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { Calendar } from 'src/components/ui/calendar';
import { FormField, Section } from './common';
import { AddressFields } from './AddressFields';

const DatePickerWithYear = ({ field, error }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  // Parse the date string in DD/MM/YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split('/');
    return new Date(year, month - 1, day); // month is 0-based
  };

  // Format date to DD/MM/YYYY
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  const selectedDate = field.value ? parseDate(field.value) : undefined;
  const defaultMonth = selectedDate || new Date(currentYear - 18, 0, 1);

  const today = new Date();
  const hundredYearsAgo = subYears(today, 100);
  const eighteenYearsAgo = subYears(today, 18);

  const handleSelect = (date) => {
    if (date) {
      field.onChange(formatDate(date)); // Store date as DD/MM/YYYY string
      setCalendarOpen(false);
    }
  };

  const errorClass = error
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : '';

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverTrigger asChild>
        <Button
          data-field="dob"
          variant="outline"
          className={cn(
            'w-full justify-between text-left font-normal h-10',
            !selectedDate && 'text-muted-foreground',
            errorClass
          )}
        >
          <span className="truncate">
            {selectedDate
              ? format(selectedDate, 'dd/MM/yyyy')
              : 'Pick your date of birth'}
          </span>
          <CalendarIcon className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={
            (date) =>
              date > eighteenYearsAgo || // Cannot select dates less than 18 years ago
              date < hundredYearsAgo // Cannot select dates more than 100 years ago
          }
          initialFocus
          defaultMonth={defaultMonth}
          fromYear={currentYear - 100} // Earliest selectable year
          toYear={currentYear - 18} // Latest selectable year
          captionLayout="dropdown-buttons"
          classNames={{
            caption_label: 'hidden',
            dropdown_month: 'flex justify-center text-sm font-medium',
            dropdown_year: 'flex justify-center text-sm font-medium',
            dropdown_icon: 'hidden',
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
export const PersonalInfoSection = ({
  control,
  errors,
  masterData,
  setValue,
  hidePersonalDetails = false,
  disableContactFields = false,
  hideTitle = false,
  hideNameFields = false,
  hideContactRow = false,
}) => (
  <Section
    title="Personal Information"
    className="p-6 border-none !p-0"
    icon={<UserIcon className="mr-2 h-4 w-4 text-blue-700" />}
    hideTitle={hideTitle}
  >
    <div className="relative">
      {/* Name Fields */}
      <div className="space-y-4 md:space-y-4 bg-ah-surface-page">
        {!hideNameFields && (
          <div className="grid grid-cols-12 gap-4 md:gap-4">
            <div className="col-span-12 md:col-span-4">
              <FormField
                className="text-xs"
                label="First Name"
                id="firstname"
                required
              >
                <Controller
                  name="firstname"
                  control={control}
                  rules={{
                    required: true,
                    pattern: /^[A-Za-z\s]+$/,
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="firstname"
                      className={`${
                        errors?.firstname
                          ? 'border-red-500 focus:border-red-500'
                          : ''
                      }text-xs`}
                      placeholder="Enter your first name"
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="col-span-12 md:col-span-4">
              <FormField
                className="text-xs"
                label="Middle Name"
                id="middlename"
              >
                <Controller
                  name="middlename"
                  control={control}
                  rules={{
                    required: false,
                    pattern: /^[A-Za-z\s]*$/,
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`${
                        errors?.middlename
                          ? 'border-red-500 focus:border-red-500'
                          : ''
                      }text-xs`}
                      placeholder="Enter your middle name"
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="col-span-12 md:col-span-4">
              <FormField
                className="text-xs"
                label="Last Name"
                id="lastname"
                required
              >
                <Controller
                  name="lastname"
                  control={control}
                  rules={{
                    required: true,
                    pattern: /^[A-Za-z\s]+$/,
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`${
                        errors?.lastname
                          ? 'border-red-500 focus:border-red-500'
                          : ''
                      }text-xs`}
                      placeholder="Enter your last name"
                    />
                  )}
                />
              </FormField>
            </div>
          </div>
        )}

        {/* Contact Details */}
        {!hideContactRow && (
          <div className="grid grid-cols-12 gap-4 md:gap-4">
            <div className="col-span-12 md:col-span-6">
              <FormField className="text-xs" label="Email">
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: false,
                    validate: (value) => {
                      // Only validate pattern if email is provided
                      if (!value || value.trim() === '') {
                        return true; // Empty email is allowed
                      }
                      const emailPattern =
                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                      return (
                        emailPattern.test(value) || 'Invalid email address'
                      );
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="email"
                      disabled={disableContactFields}
                      className={`${
                        errors?.email
                          ? 'border-red-500 focus:border-red-500'
                          : ''
                      } ${disableContactFields ? 'bg-gray-50 cursor-not-allowed' : ''} text-xs`}
                      placeholder="Enter your email"
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="col-span-12 md:col-span-6">
              <FormField className="text-xs" label="Contact" required>
                <Controller
                  name="contact"
                  control={control}
                  rules={{
                    required: true,
                    pattern: /^[0-9]{10}$/,
                  }}
                  render={({ field }) => (
                    <div className="flex w-full">
                      <div
                        className={`${
                          errors?.contact ? 'border-red-500' : 'border-input'
                        } flex items-center rounded-l-md border ${
                          disableContactFields
                            ? 'bg-gray-50'
                            : 'bg-ah-surface-form-section'
                        } px-3 text-xs text-muted-foreground`}
                      >
                        +91
                      </div>
                      <Input
                        {...field}
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        disabled={disableContactFields}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value.replace(/\D/g, '').slice(0, 10)
                          )
                        }
                        className={`${
                          errors?.contact
                            ? 'border-red-500 focus:border-red-500'
                            : ''
                        } ${
                          disableContactFields
                            ? 'bg-gray-50 cursor-not-allowed'
                            : ''
                        } text-xs rounded-l-none border-l-0`}
                        placeholder="10-digit mobile"
                      />
                    </div>
                  )}
                />
              </FormField>
            </div>
          </div>
        )}

        {/* Personal Details - Conditionally rendered */}
        {!hidePersonalDetails && (
          <div className="grid grid-cols-12 gap-4 md:gap-4">
            <div className="col-span-12 md:col-span-6">
              <FormField className="text-xs" label="Date of Birth" required>
                <Controller
                  name="dob"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      const date = new Date(value);
                      const today = new Date();
                      const minAge = subYears(today, 18); // Must be at least 18 years old
                      const maxAge = subYears(today, 100); // Maximum age 100 years

                      if (date > minAge) {
                        return 'You must be at least 18 years old';
                      }
                      if (date < maxAge) {
                        return 'Date cannot be more than 100 years ago';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <DatePickerWithYear
                      field={field}
                      error={errors?.dob}
                      maxDate={subYears(new Date(), 18)} // Restrict future date selection to 18 years ago
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="col-span-12 md:col-span-6">
              <FormField className="text-xs" label="Father's Name" required>
                <Controller
                  name="fathersname"
                  control={control}
                  rules={{
                    required: true,
                    pattern: /^[A-Za-z\s]+$/,
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`${
                        errors?.fathersname
                          ? 'border-red-500 focus:border-red-500'
                          : ''
                      }text-xs`}
                      placeholder="Enter your father's name"
                    />
                  )}
                />
              </FormField>
            </div>
          </div>
        )}

        {/* Address */}
        <AddressFields
          control={control}
          errors={errors}
          masterData={masterData}
          setValue={setValue}
        />
      </div>
    </div>
  </Section>
);
